import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ChevronDown, Zap, Swords, ShieldAlert, Sparkles, Eye,
  BookOpen, Lightbulb, Users, Link2, Loader2, CircleHelp, CheckCircle2,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { fetchFruit, CANON_COLORS, CAT_COLORS, imgSrc } from "../lib/api";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const Field = ({ label, children }) => (
  <div>
    <div className="label-mono luma-muted mb-1">{label}</div>
    <div className="text-sm luma-ink font-semibold">{children}</div>
  </div>
);

const Section = ({ icon: Icon, title, children, color = "var(--luma-purple)", collapsible = false, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="luma-card p-5 sm:p-6 rise" data-testid={testid}>
      <button type="button" disabled={!collapsible}
        onClick={() => collapsible && setOpen(!open)}
        className="w-full flex items-center gap-2.5 text-left">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${color}1a`, color }}>
          <Icon size={18} />
        </span>
        <h2 className="font-display font-bold text-lg luma-ink flex-1">{title}</h2>
        {collapsible && (
          <ChevronDown size={18} className={`luma-muted transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </section>
  );
};

const List = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2 text-sm luma-ink">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--luma-pink)" }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const Para = ({ text }) => <p className="text-sm luma-ink leading-relaxed whitespace-pre-line">{text}</p>;

const NA = () => <span className="luma-muted italic">Informazione sconosciuta</span>;

export default function FruitDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: f, isLoading, isError } = useQuery({
    queryKey: ["fruit", slug], queryFn: () => fetchFruit(slug),
  });

  useEffect(() => {
    if (!f) return;
    const title = `${f.nome_romaji}${f.nome_it ? ` (${f.nome_it})` : ""} | Fruttipedia`;
    document.title = title;
    const desc = (f.poteri || "Scheda del Frutto del Diavolo su Fruttipedia.").slice(0, 155);
    setMeta("description", desc);
    const ld = {
      "@context": "https://schema.org", "@type": "Article",
      headline: f.nome_romaji, description: desc, inLanguage: "it",
      about: "One Piece Devil Fruit",
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "ld-json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
    window.scrollTo(0, 0);
  }, [f]);

  if (isLoading) {
    return (
      <div className="App"><Header />
        <div className="flex justify-center py-24"><Loader2 className="animate-spin" style={{ color: "var(--luma-action)" }} size={36} /></div>
      </div>
    );
  }

  if (isError || !f) {
    return (
      <div className="App"><Header />
        <div className="max-w-xl mx-auto px-4 py-20 text-center">
          <div className="luma-card p-10">
            <p className="font-display font-bold text-xl luma-ink">Frutto non trovato</p>
            <Link to="/catalogo" className="pill-primary inline-flex px-5 py-2.5 mt-5 text-sm">Torna al catalogo</Link>
          </div>
        </div>
      </div>
    );
  }

  const canon = CANON_COLORS[f.canonicita] || CANON_COLORS["Incerto"];
  const cat = CAT_COLORS[f.categoria] || CAT_COLORS["Sconosciuta"];
  const owner = (f.possessore_attuale || "").trim();
  const ownerKnown = owner && !owner.toLowerCase().includes("sconosciut");

  return (
    <div className="App">
      <Header />

      <main className="w-[min(820px,calc(100%-32px))] mx-auto pt-4 pb-4">
        <nav className="text-sm luma-muted flex items-center gap-1.5 mb-3 flex-wrap" data-testid="breadcrumb">
          <Link to="/" className="hover:underline">Fruttipedia</Link>
          <span>/</span>
          <Link to="/catalogo" className="hover:underline">Tutti i frutti</Link>
          <span>/</span>
          <span className="luma-ink font-semibold truncate">{f.nome_romaji}</span>
        </nav>

        <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/catalogo"))} data-testid="back-to-catalog"
          className="pill-soft px-4 py-2 text-sm flex items-center gap-2 mb-4">
          <ArrowLeft size={15} /> Torna al catalogo
        </button>

        {/* Hero */}
        <div className="luma-card p-6 sm:p-7 mb-4 rise" data-testid="fruit-hero">
          <div className="flex items-start gap-4">
            <span className="font-display font-bold flex items-center justify-center rounded-2xl shrink-0"
              style={{ width: 72, height: 72, color: "#fff", fontSize: 30,
                backgroundImage: `linear-gradient(140deg, ${cat}, ${cat}cc)`,
                boxShadow: `0 12px 26px -10px ${cat}aa` }}>
              {(f.categoria || "?")[0]}
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="font-display font-bold text-2xl sm:text-3xl luma-ink leading-tight" data-testid="fruit-name">
                {f.nome_romaji}
              </h1>
              {f.nome_jp && <p className="text-sm luma-muted mt-0.5">{f.nome_jp}</p>}
              {f.nome_it && <p className="text-sm luma-cyan-ink font-semibold">{f.nome_it}</p>}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-4">
            <span className="chip" style={{ background: `${cat}1a`, color: cat }}>{f.categoria}</span>
            {f.sottocategoria !== "—" && <span className="chip chip-outline">{f.sottocategoria}</span>}
            <span className="chip chip-outline">Origine: {f.origine}</span>
            <span className="chip" style={{ background: canon.bg, color: canon.color }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: canon.dot }} />{f.canonicita}
            </span>
            <span className="chip chip-outline">
              {f.verificato ? <><CheckCircle2 size={12} /> Verificato</> : <><CircleHelp size={12} /> Da verificare</>}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Identità */}
          <Section icon={BookOpen} title="Nome e classificazione" color={cat} testid="section-identita">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome romanizzato">{f.nome_romaji}</Field>
              <Field label="Nome giapponese">{f.nome_jp || <NA />}</Field>
              <Field label="Nome italiano">{f.nome_it || <NA />}</Field>
              <Field label="Nomi alternativi">{f.nomi_alternativi.length ? f.nomi_alternativi.join(", ") : <NA />}</Field>
              <Field label="Significato">{f.significato || <NA />}</Field>
              <Field label="Stato informazioni">{f.stato_info}</Field>
            </div>
          </Section>

          {/* Possessori */}
          <Section icon={Users} title="Possessori e cronologia" color="#0ea5e9" testid="section-possessori">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Possessore attuale">
                {ownerKnown ? owner : <span className="luma-muted italic">Possessore sconosciuto</span>}
              </Field>
              <Field label="Possessori precedenti">
                {f.possessori_precedenti.length ? f.possessori_precedenti.join(", ") : <NA />}
              </Field>
              <Field label="Stato del frutto">{f.stato_frutto || <NA />}</Field>
              <Field label="Risveglio">{f.risveglio}</Field>
            </div>
            {f.cronologia && (
              <div className="mt-4">
                <div className="label-mono luma-muted mb-1">Cronologia dei passaggi</div>
                <Para text={f.cronologia} />
              </div>
            )}
          </Section>

          {/* Poteri */}
          <Section icon={Zap} title="Poteri" color="#f59e0b" collapsible testid="section-poteri">
            {f.poteri ? <Para text={f.poteri} /> : <NA />}
            {f.capacita.length > 0 && (
              <div className="mt-4">
                <div className="label-mono luma-muted mb-2">Capacità principali</div>
                <List items={f.capacita} />
              </div>
            )}
          </Section>

          {f.tecniche.length > 0 && (
            <Section icon={Swords} title="Tecniche conosciute" color="#ec4899" collapsible testid="section-tecniche">
              <div className="flex flex-wrap gap-1.5">
                {f.tecniche.map((t, i) => <span key={i} className="chip chip-outline">{t}</span>)}
              </div>
            </Section>
          )}

          {/* Limiti e debolezze */}
          <Section icon={ShieldAlert} title="Limiti e debolezze" color="#ef4444" collapsible testid="section-limiti">
            <div className="space-y-4">
              <div>
                <div className="label-mono luma-muted mb-1">Limiti</div>
                {f.limiti ? <Para text={f.limiti} /> : <NA />}
              </div>
              <div>
                <div className="label-mono luma-muted mb-1">Debolezze</div>
                {f.debolezze ? <Para text={f.debolezze} /> : <NA />}
              </div>
            </div>
          </Section>

          {/* Risveglio */}
          <Section icon={Sparkles} title="Risveglio" color="#8b5cf6" testid="section-risveglio">
            <Para text={`Stato del risveglio: ${f.risveglio}.`} />
          </Section>

          {/* Aspetto */}
          <Section icon={Eye} title="Aspetto fisico" color="#0ea5e9" testid="section-aspetto">
            <div className="space-y-3">
              {f.immagine && (
                <div className="rounded-xl overflow-hidden flex items-center justify-center mb-1"
                  style={{ background: "#F7FBFF", border: "1px solid var(--luma-line)", maxHeight: 360 }}
                  data-testid="fruit-detail-image">
                  <img src={imgSrc(f.immagine)} alt={`Frutto ${f.nome_romaji}`} loading="lazy"
                    className="w-full object-contain" style={{ maxHeight: 360 }} />
                </div>
              )}
              <Para text={f.aspetto} />
              {f.colore && <Field label="Colore">{f.colore}</Field>}
              {!f.immagine && (
                <p className="text-xs luma-muted italic">
                  Nessuna immagine del frutto fisico è mostrata: non esiste una rappresentazione
                  documentata e utilizzabile. La scheda resta completa anche senza immagine.
                </p>
              )}
            </div>
          </Section>

          {/* Fonti */}
          <Section icon={BookOpen} title="Prima apparizione e fonti" color={cat} collapsible testid="section-fonti">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Prima apparizione">{f.prima_apparizione || <NA />}</Field>
              <Field label="Capitolo del manga">{f.capitolo_manga || <NA />}</Field>
              <Field label="Episodio dell'anime">{f.episodio_anime || <NA />}</Field>
              <Field label="Ultima verifica">{f.ultima_verifica}</Field>
            </div>
            <div className="mt-4">
              <div className="label-mono luma-muted mb-2">Provenienza</div>
              <div className="flex flex-wrap gap-1.5">
                {f.fonte.length ? f.fonte.map((s, i) => <span key={i} className="chip chip-outline">{s}</span>) : <NA />}
              </div>
            </div>
          </Section>

          {/* Curiosità */}
          {f.curiosita && (
            <Section icon={Lightbulb} title="Curiosità" color="#f59e0b" collapsible testid="section-curiosita">
              <Para text={f.curiosita} />
            </Section>
          )}

          {/* Correlati */}
          {f.correlati.length > 0 && (
            <Section icon={Link2} title="Frutti e personaggi correlati" color="#0ea5e9" testid="section-correlati">
              <div className="flex flex-wrap gap-1.5">
                {f.correlati.map((c, i) => <span key={i} className="chip chip-outline">{c}</span>)}
              </div>
            </Section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
